import { getCurrentUser } from '../api/authService';
import { extractRegistrationListData } from '../api/articleService';
import { BookOpen, HelpCircle, Settings, Shield, Rocket } from 'lucide-react';

/** Tenant scope for portal KB — JWT first, then session (same as articleService / MyArticles). */
export function resolveKbTenantId() {
  const user = getCurrentUser();
  if (user?.tenantId != null && user.tenantId !== '') {
    const n = Number(user.tenantId);
    if (Number.isFinite(n)) return n;
  }
  if (typeof sessionStorage !== 'undefined') {
    const raw = sessionStorage.getItem('tenantId');
    if (raw) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

export function isPublishedKbArticle(article) {
  const status = (article?.status || '').toLowerCase();
  return status === 'active' || status === 'published';
}

function articleRating(helpful, notHelpful) {
  const total = helpful + notHelpful;
  if (total === 0) return 0;
  return Math.round((helpful / total) * 5 * 10) / 10;
}

/** Map registration API row to UnifiedKnowledgeBase / KnowledgeBaseAccess card model. */
export function mapKbArticleForPortal(a) {
  const helpful = a.helpfulCount ?? 0;
  const notHelpful = a.notHelpfulCount ?? 0;
  const rawCat = (a.category || '').trim() || 'General';
  const content = a.answer || '';
  const excerpt = (a.question || '').slice(0, 280) || content.slice(0, 280);
  const wordCount = (content || excerpt).split(/\s+/).filter(Boolean).length;
  const readMins = Math.max(1, Math.ceil(wordCount / 200));

  return {
    id: a.articleId ?? a.id,
    articleId: a.articleId ?? a.id,
    title: a.title || 'Untitled',
    question: a.question || '',
    excerpt,
    summary: excerpt,
    content,
    category: rawCat,
    categoryKey: rawCat.toLowerCase().replace(/\s+/g, '-'),
    readTime: `${readMins} min read`,
    views: a.usedCount ?? 0,
    helpfulCount: helpful,
    notHelpfulCount: notHelpful,
    helpfulVotes: helpful,
    notHelpfulVotes: notHelpful,
    rating: articleRating(helpful, notHelpful),
    tags: Array.isArray(a.tags) ? a.tags : [],
    author: a.createdBy || 'Support',
    updatedAt: a.updatedAt || a.createdAt,
    lastUpdated: a.updatedAt || a.createdAt,
    status: a.status,
    attachments: Array.isArray(a.attachments) ? a.attachments : [],
  };
}

/** Single article from getArticleById response. */
export function extractArticleDetail(serviceResponse) {
  const body = serviceResponse?.data;
  if (!body) return null;
  if (body.data != null && typeof body.data === 'object' && !Array.isArray(body.data)) {
    return body.data;
  }
  if (body.articleId != null || body.id != null) return body;
  return null;
}

export function filterPublishedKbArticles(rawList) {
  return (rawList || []).filter(isPublishedKbArticle);
}

export function parsePublishedKbList(serviceResponse) {
  return filterPublishedKbArticles(extractRegistrationListData(serviceResponse)).map(mapKbArticleForPortal);
}

const CATEGORY_ICONS = [BookOpen, HelpCircle, Settings, Shield, Rocket];
const CATEGORY_COLORS = ['bg-primary', 'bg-secondary', 'bg-accent', 'bg-destructive', 'bg-primary'];

/** Category cards for UnifiedKnowledgeBase grid (live distinct categories only). */
export function buildKbCategoryCards(articles) {
  const counts = new Map();
  (articles || []).forEach((article) => {
    const name = (article.category || '').trim();
    if (!name) return;
    counts.set(name, (counts.get(name) || 0) + 1);
  });

  return [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([title, articleCount], index) => ({
      title,
      description: `Articles in ${title}`,
      articleCount,
      icon: CATEGORY_ICONS[index % CATEGORY_ICONS.length],
      color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
      categoryKey: title.toLowerCase().replace(/\s+/g, '-'),
    }));
}

/** Sidebar categories for KnowledgeBaseAccess modal. */
export function buildKbAccessCategories(articles) {
  return buildKbCategoryCards(articles).map((card) => ({
    id: card.categoryKey,
    name: card.title,
    articleCount: card.articleCount,
    subcategories: [],
  }));
}

/** Sidebar tag filters derived from live article tags. */
export function buildKbTagFilters(articles) {
  const counts = new Map();
  (articles || []).forEach((article) => {
    (article.tags || []).forEach((tag) => {
      const name = (tag || '').trim();
      if (!name) return;
      counts.set(name, (counts.get(name) || 0) + 1);
    });
  });

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, count]) => ({ name, count }));
}

/** Shape for ViewArticleModal from portal list row. */
export function toViewArticleModalShape(article) {
  if (!article) return null;
  return {
    articleId: article.articleId ?? article.id,
    id: article.articleId ?? article.id,
    title: article.title,
    question: article.question,
    answer: article.content,
    content: article.content,
    category: article.category,
    tags: article.tags,
    author: article.author,
    status: 'active',
    views: article.views,
    helpful: article.helpfulCount ?? 0,
    lastModified: article.lastUpdated || article.updatedAt,
  };
}

export function computeKbStats(articles) {
  const list = articles || [];
  const totalViews = list.reduce((sum, a) => sum + (a.views || 0), 0);
  const ratings = list
    .map((a) => {
      const helpful = a.helpfulCount || 0;
      const notHelpful = a.notHelpfulCount || 0;
      const total = helpful + notHelpful;
      return total > 0 ? (helpful / total) * 5 : null;
    })
    .filter((r) => r != null);

  const averageRating = ratings.length
    ? Math.round((ratings.reduce((sum, r) => sum + r, 0) / ratings.length) * 10) / 10
    : 0;

  return {
    totalArticles: list.length,
    totalViews,
    averageRating,
    searchSuccessRate: list.length > 0 ? 100 : 0,
  };
}
